// CreateRegistrationWizard.tsx
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Title, Stack, Group, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService, useFacilitatorService, useACCourseService,
  FacilitatorModelView, CreateRegistration, Member, CourseDateModelView,
} from '@actschurch/ccms-sdk';
import MultiStepForm, { FormStep } from '@/components/advanced/forms/MultiStepForm';
import { DynamicFormHandle } from '@/components/advanced/FormBuilder';

import { ParticipantDraft } from './types';
import { emptyParticipant, facilitatorName } from './helpers';
import { ParticipantsStep } from '../Form/Steps/ParticipantsStep';
import { CourseSelectionStep } from '../Form/Steps/CourseSelectionStep';
import { CourseDetailsStep } from '../Form/Steps/CourseDetailsStep';
import { FacilitatorsStep } from '../Form/Steps/FacilitatorsStep';
import { ReviewStep } from '../Form/Steps/ReviewStep';

export default function CreateRegistrationWizard() {
  const navigate = useNavigate();

  const registrationService = useRegistrationService();
  const courseService       = useACCourseService();
  const Facilitators        = useFacilitatorService();

  // ── Courses & dates ──────────────────────────────────────────────────────
  const [courses, setCourses]               = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseID, setCourseID]             = useState<string | null>(null);

  const [courseDates, setCourseDates]   = useState<CourseDateModelView[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [dateID, setDateID]             = useState<string | null>(null);

  const [description, setDescription] = useState('');
  const descriptionEditedRef = useRef(false);

  // ── Participants ──────────────────────────────────────────────────────────
  const [participants, setParticipants] = useState<ParticipantDraft[]>([emptyParticipant()]);
  const [searchTerms, setSearchTerms]   = useState<string[]>(['']);

  // ── Course Details (course-level custom fields) ──────────────────────────
  // Collected here in the wizard's own state, since the registration doesn't
  // exist yet — persisted to the backend only after creation succeeds
  // (see handleCreate below).
  const [courseDetailValues, setCourseDetailValues] = useState<Record<string, any>>({});
  // Raw definitions from CourseDetailsStep — needed to map fieldKey -> the
  // real courseCustomFieldID for CreateRegistrationCourseCustomData payloads.
  const [courseCustomFieldDefs, setCourseCustomFieldDefs] = useState<any[]>([]);

  const courseDetailsFormRef = useRef<DynamicFormHandle>(null);
  // Reset any shown errors whenever the course changes, since the field set changes
  useEffect(() => {
    setCourseDetailValues({});
    courseDetailsFormRef.current?.clearErrors();
  }, [courseID]);

  // ── Auto-fill description from participant first names ────────────────────
// e.g. "Alex & Sindy" — regenerates as participants change, until the user
// manually edits the description field themselves.

useEffect(() => {
  if (descriptionEditedRef.current) return;

  const names = participants
    .map((p) => [p.name.trim()].filter(Boolean).join(' '))
    .filter(Boolean);

  const autoDescription = names.join(' & ');
  setDescription(autoDescription);
}, [participants]);

const handleDescriptionChange = (value: string) => {
  descriptionEditedRef.current = true;
  setDescription(value);
};


  // ── Facilitators ─────────────────────────────────────────────────────────
  const [allFacilitators, setAllFacilitators]         = useState<FacilitatorModelView[]>([]);
  const [loadingFacilitators, setLoadingFacilitators] = useState(false);
  const [facilitatorUserIds, setFacilitatorUserIds]   = useState<string[]>([]);
  const [leadFacilitatorUserId, setLeadFacilitatorUserId] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);

  // ── Fetch all courses on mount ────────────────────────────────────────────
  useEffect(() => {
    setLoadingCourses(true);
    courseService.getAll()
      .then(setCourses)
      .catch(() => toast.error('Error', 'Could not load courses.'))
      .finally(() => setLoadingCourses(false));
  }, [courseService]);

  // ── Fetch dates whenever the selected course changes ─────────────────────
  useEffect(() => {
    setDateID(null);
    setCourseDates([]);
    if (!courseID) return;
    setLoadingDates(true);
    courseService.getDates(Number(courseID))
      .then(setCourseDates)
      .catch(() => toast.error('Error', 'Could not load dates for this course.'))
      .finally(() => setLoadingDates(false));
  }, [courseID, courseService]);

  // ── Fetch facilitators on mount ───────────────────────────────────────────
  useEffect(() => {
    setLoadingFacilitators(true);
    Facilitators.list()
      .then(setAllFacilitators)
      .catch(() => toast.error('Error', 'Could not load facilitator list.'))
      .finally(() => setLoadingFacilitators(false));
  }, [Facilitators]);

  // ── Participant helpers ───────────────────────────────────────────────────
  const updateParticipant = (index: number, patch: Partial<ParticipantDraft>) =>
    setParticipants((prev) =>
      prev.map((p, i) => {
        if (i !== index) return patch.isMain ? { ...p, isMain: false } : p;
        return { ...p, ...patch };
      })
    );

  const updateSearchTerm = (index: number, val: string) =>
    setSearchTerms((prev) => prev.map((t, i) => (i === index ? val : t)));

  const handleMemberSelect = (index: number, member: Member) => {
    updateParticipant(index, {
      memberID:  member.memberID,
      name:      member.firstName ?? '',
      surname:   member.lastName ?? '',
      email:     member.email ?? '',
      phone:     member.cellNumber ?? member.telephone ?? '',
      gender:    member.gender ?? null,
      resolved:  true,
    });
    updateSearchTerm(index, [member.firstName, member.lastName].filter(Boolean).join(' '));
  };

  const handleClearParticipant = (index: number) => {
    setParticipants((prev) =>
      prev.map((p, i) =>
        i !== index ? p : { ...emptyParticipant(), isMain: p.isMain, hasConsent: p.hasConsent }
      )
    );
    updateSearchTerm(index, '');
  };

  const addParticipant = () => {
    setParticipants((prev) => [...prev, emptyParticipant()]);
    setSearchTerms((prev) => [...prev, '']);
  };

  const removeParticipant = (index: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== index));
    setSearchTerms((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Facilitators ──────────────────────────────────────────────────────────
  const facilitatorData = allFacilitators.map((f) => ({ value: f.userId, label: facilitatorName(f) }));

  const handleFacilitatorChange = (ids: string[]) => {
    setFacilitatorUserIds(ids);
    if (leadFacilitatorUserId && !ids.includes(leadFacilitatorUserId))
      setLeadFacilitatorUserId(null);
  };

  // ── Derived / review helpers ────────────────────────────────────────────
  const selectedCourse = courses.find((c) => String(c.courseID) === courseID);
  const selectedDate   = courseDates.find((d) => String(d.dateID) === dateID);
  const validParticipants = participants.filter((p) => p.name.trim() && p.surname.trim());
  const mainParticipant   = validParticipants.find((p) => p.isMain);
  const selectedFacilitators = allFacilitators.filter((f) => facilitatorUserIds.includes(f.userId));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!courseID) { toast.error('Missing Course', 'Please select a course.');       return; }
    if (!dateID)   { toast.error('Missing Date',   'Please select an intake date.'); return; }

    if (validParticipants.length === 0) {
      toast.error('Missing Participants', 'Add at least one participant with a name and surname.');
      return;
    }

    const payload: CreateRegistration = {
      courseID:              Number(courseID),
      dateID:                Number(dateID),
      description:           description || undefined,
      participants:          validParticipants.map((p) => ({
        memberID:   p.memberID ?? undefined,
        name:       p.name,
        surname:    p.surname,
        email:      p.email,
        phone:      p.phone,
        gender:     p.gender ?? undefined,
        isMain:     p.isMain,
        hasConsent: p.hasConsent,
      })),
      facilitatorUserIds:    facilitatorUserIds.length ? facilitatorUserIds : [],
      leadFacilitatorUserId: leadFacilitatorUserId ?? undefined,
    };

    setSaving(true);
    try {
      const created = await registrationService.create(payload);

      // CreateRegistrationCourseCustomData actually wants { courseCustomFieldID, memberID?, value? } —
      // not fieldKey — so map each answered fieldKey back to its real
      // courseCustomFieldID using the definitions CourseDetailsStep loaded.
      const fieldIDByKey = new Map(
        courseCustomFieldDefs
          .filter((d) => d.fieldKey)
          .map((d) => [d.fieldKey as string, d.courseCustomFieldID as number])
      );
      const answeredEntries = Object.entries(courseDetailValues).filter(
        ([, v]) => v !== undefined && v !== null && v !== ''
      );
      if (answeredEntries.length > 0 && created?.registrationID) {
        await Promise.all(
          answeredEntries
            .map(([fieldKey, raw]) => {
              const courseCustomFieldID = fieldIDByKey.get(fieldKey);
              if (courseCustomFieldID === undefined) return null; // no matching definition — skip
              const value = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
              return registrationService.addCourseCustomData(created.registrationID, {
                courseCustomFieldID,
                memberID: mainParticipant?.memberID ?? undefined,
                value,
              });
            })
            .filter((p): p is Promise<any> => p !== null)
        );
      }

      toast.success('Registration Created', 'The registration has been created successfully.');
      navigate('/Grow/Registration');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to create registration.');
    } finally {
      setSaving(false);
    }
  };

  // ── Step validation ──────────────────────────────────────────────────────
  const validateStep1 = () => {
    if (validParticipants.length === 0) {
      toast.error('Missing Participants', 'Add at least one participant with a name and surname.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!courseID) { toast.error('Missing Course', 'Please select a course.'); return false; }
    if (!dateID)   { toast.error('Missing Date', 'Please select an intake date.'); return false; }
    return true;
  };

  const validateStep3 = () => {
    return courseDetailsFormRef.current?.validate() ?? true;
  };

  // ── Steps ─────────────────────────────────────────────────────────────────
  const steps: FormStep[] = [
    {
      title: 'Members',
      description: 'Search & add participants',
      validate: validateStep1,
      content: (
        <ParticipantsStep
          participants={participants}
          searchTerms={searchTerms}
          onUpdateSearchTerm={updateSearchTerm}
          onMemberSelect={handleMemberSelect}
          onUpdateParticipant={updateParticipant}
          onClearParticipant={handleClearParticipant}
          onAddParticipant={addParticipant}
          onRemoveParticipant={removeParticipant}
        />
      ),
    },
    {
      title: 'Course',
      description: 'Course, date & description',
      validate: validateStep2,
      content: (
       <CourseSelectionStep
          courses={courses}
          loadingCourses={loadingCourses}
          courseID={courseID}
          onCourseIDChange={setCourseID}
          courseDates={courseDates}
          loadingDates={loadingDates}
          dateID={dateID}
          onDateIDChange={setDateID}
          description={description}
          onDescriptionChange={handleDescriptionChange}
        />
      ),
    },
    {
      title: 'Course Details',
      description: 'Additional questions for this course',
      validate: validateStep3,
      content: (
        <CourseDetailsStep
          ref={courseDetailsFormRef}
          courseID={courseID ? Number(courseID) : null}
          values={courseDetailValues}
          onChange={(key, value) => setCourseDetailValues((prev) => ({ ...prev, [key]: value }))}
          onDefinitionsLoaded={setCourseCustomFieldDefs}
        />
      ),
    },
    {
      title: 'Facilitators',
      description: 'Assign facilitators',
      content: (
        <FacilitatorsStep
          facilitatorData={facilitatorData}
          loadingFacilitators={loadingFacilitators}
          facilitatorUserIds={facilitatorUserIds}
          onFacilitatorChange={handleFacilitatorChange}
          leadFacilitatorUserId={leadFacilitatorUserId}
          onLeadFacilitatorChange={setLeadFacilitatorUserId}
        />
      ),
    },
    {
      title: 'Review',
      description: 'Confirm & submit',
      content: (
        <ReviewStep
        validParticipants={validParticipants}
        mainParticipant={mainParticipant}
        selectedCourse={selectedCourse}
        selectedDate={selectedDate}
        description={description}
        selectedFacilitators={selectedFacilitators}
        leadFacilitatorUserId={leadFacilitatorUserId}
        courseDetailValues={courseDetailValues}
        courseDefinitions={courseCustomFieldDefs}
      />
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="flex-start" mb="lg">
        <Stack gap={4}>
          <Title order={2}>New Registration</Title>
          <Text c="dimmed">Create a new course registration.</Text>
        </Stack>
        <Group
          gap={6}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/Grow/Registration')}
        >
          <IconArrowLeft size={16} />
          <Text size="sm" c="blue">Back to Registrations</Text>
        </Group>
      </Group>

      <MultiStepForm
        steps={steps}
        onComplete={handleCreate}
        onCancel={() => navigate('/Grow/Registration')}
        completeLabel="Create Registration"
        loading={saving}
        allowSkip={false}
      />
    </Container>
  );
}